import type { GameMode } from '@/state/types';

/**
 * Phase 7.1.1 — Save-Slot meta index.
 *
 * Each game-state Zustand store writes to `rescueop_save_v8_slot_${id}` (handled
 * inside gameStore.ts via the persist `name`-factory). This module tracks a
 * lightweight summary of every slot so the SaveBrowser can render a list
 * without parsing the full per-slot blob.
 */

export const SLOT_COUNT = 3;
export const META_INDEX_KEY = 'rescueop_save_index_v1';
/** Used by gameStore — single source of truth for the slot key shape. */
export function slotKey(id: number): string {
  return `rescueop_save_v8_slot_${id}`;
}

export interface SlotMeta {
  id: number;
  cityName: string | null;
  mode: GameMode;
  level: number;
  credits: number;
  /** Wall-clock ms timestamp of the last write. */
  lastPlayed: number;
}

/** Read all slot metas. Returns 3 entries (slot 1/2/3) — empty entry = id only. */
export function readSlotMetas(): SlotMeta[] {
  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultMetas();
  }
  try {
    const raw = window.localStorage.getItem(META_INDEX_KEY);
    if (!raw) return defaultMetas();
    const parsed = JSON.parse(raw) as SlotMeta[];
    if (!Array.isArray(parsed)) return defaultMetas();
    // Normalise: ensure 3 entries in id order.
    const map = new Map<number, SlotMeta>();
    for (const m of parsed) if (m && typeof m.id === 'number') map.set(m.id, m);
    return defaultMetas().map((d) => map.get(d.id) ?? d);
  } catch {
    return defaultMetas();
  }
}

function defaultMetas(): SlotMeta[] {
  return Array.from({ length: SLOT_COUNT }, (_, i) => ({
    id: i + 1,
    cityName: null,
    mode: 'normal' as GameMode,
    level: 1,
    credits: 0,
    lastPlayed: 0,
  }));
}

export function writeSlotMeta(meta: SlotMeta): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const all = readSlotMetas();
  const idx = all.findIndex((m) => m.id === meta.id);
  if (idx >= 0) all[idx] = meta;
  else all.push(meta);
  try {
    window.localStorage.setItem(META_INDEX_KEY, JSON.stringify(all));
  } catch {
    // quota or disabled — non-fatal
  }
}

/** Returns true if this slot has any meaningful save data. */
export function isSlotOccupied(meta: SlotMeta): boolean {
  return meta.cityName !== null;
}

/** Wipe a slot's persisted state and meta entry. */
export function deleteSlot(id: number): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(slotKey(id));
  } catch {
    // ignore
  }
  const all = readSlotMetas();
  const next = all.map((m) =>
    m.id === id
      ? { ...m, cityName: null, mode: 'normal' as GameMode, level: 1, credits: 0, lastPlayed: 0 }
      : m,
  );
  try {
    window.localStorage.setItem(META_INDEX_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

/** Reads the active-slot pointer, defaulting to slot 1. */
export const ACTIVE_SLOT_KEY = 'rescueop_active_slot_v1';

export function readActiveSlotId(): number {
  if (typeof window === 'undefined' || !window.localStorage) return 1;
  try {
    const raw = window.localStorage.getItem(ACTIVE_SLOT_KEY);
    if (!raw) return 1;
    const id = Number(raw);
    if (!Number.isFinite(id) || id < 1 || id > SLOT_COUNT) return 1;
    return id;
  } catch {
    return 1;
  }
}

export function writeActiveSlotId(id: number): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(ACTIVE_SLOT_KEY, String(id));
  } catch {
    // ignore
  }
}
